/**
 * Vessel hull art — port of Flutter's `VesselSkin` / `VesselSkinStore`
 * (`lib/services/vessel_skin_store.dart`).
 *
 * The backend owns the value (`boat_skin` on the vessel doc). The ids are
 * persisted server-side and read by every client — never rename them.
 */
export type VesselSkin = 'sportfish' | 'downeast' | 'center_console';

/** Ids stored before the icons were renamed; keep the artwork the owner picked. */
const LEGACY_IDS: Record<string, VesselSkin> = {
  classic: 'sportfish',
  sport: 'downeast',
  offshore: 'center_console',
};

const KNOWN: ReadonlySet<string> = new Set(['sportfish', 'downeast', 'center_console']);

export const VESSEL_SKIN_ASSET: Record<VesselSkin, string> = {
  sportfish: '/assets/vessel_skins/vessel_sportfish.svg',
  downeast: '/assets/vessel_skins/vessel_downeast.svg',
  center_console: '/assets/vessel_skins/vessel_center_console.svg',
};

/**
 * Resolve a payload `boat_skin` value.
 *
 * Absent/blank ⇒ `null` ("the payload says nothing"), NOT sportfish — Flutter's
 * `rememberStored` ignores silence for the same reason, so an unlearned boat can
 * never outvote a known one inside a bubble. A non-blank but unrecognised value
 * falls back to sportfish, like `VesselSkin.fromStored`.
 */
export function parseVesselSkin(value: unknown): VesselSkin | null {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (!v) return null;
  if (KNOWN.has(v)) return v as VesselSkin;
  return LEGACY_IDS[v] ?? 'sportfish';
}
